import { useEffect, useMemo, useRef, useState } from "react";

import { PageHeader } from "components/ui/PageHeader";
import { usePracticeHours } from "features/practice/hooks/usePracticeHours";
import { useTogglePracticeHourStatus } from "features/practice/hooks/useTogglePracticeHourStatus";
import { useUpdatePracticeHours } from "features/practice/hooks/useUpdatePracticeHours";

import styles from "./PracticeHoursDetailsPage.module.css";

const SAVE_STATUS_MESSAGES = {
  idle: "Changes save automatically",
  saving: "Saving…",
  error: "Not saved — please try again",
  invalid: "Not saved — check the highlighted day",
};

const SaveStatus = ({ status, savedAtLabel }) => {
  const hasFailed = status === "error" || status === "invalid";
  const message =
    status === "saved"
      ? `Saved ${savedAtLabel}`
      : SAVE_STATUS_MESSAGES[status] ?? SAVE_STATUS_MESSAGES.idle;

  return (
    <span
      className={`${styles.saveStatus} ${hasFailed ? styles.saveStatusError : ""}`}
      role="status"
      aria-live="polite"
      data-testid="practice-hours-save-status"
    >
      {message}
    </span>
  );
};

/*
 * `startTime`/`endTime` arrive as a Java LocalTime, which Jackson writes as [hour,
 * minute]; the control and the payload both speak "HH:mm".
 */
const convertString = (input) => {
  if (Array.isArray(input)) {
    const hours = String(input[0]).padStart(2, "0");
    const minutes = String(input[1]).padStart(2, "0");

    return `${hours}:${minutes}`;
  }

  if (typeof input === "string" && /^\d{2}:\d{2}$/.test(input)) {
    return input;
  }

  return "";
};

/*
 * Both bounds are a LocalTime on the same calendar date, so a close that is not after
 * the open cannot be stored as a range — 00:00 included, which is why midnight is
 * refused here rather than offered as the top of the day.
 */
const getDayError = (day) => {
  const from = convertString(day.startTime);
  const to = convertString(day.endTime);

  if (!from && !to) return "";
  if (!from || !to) return "Both an opening and a closing time are needed";
  if (to <= from) return "End time must be greater than the start time";

  return "";
};

const toPayloadDay = (day) => ({
  ...day,
  startTime: convertString(day.startTime),
  endTime: convertString(day.endTime),
});

const serializeHours = (hours) => JSON.stringify(hours.map(toPayloadDay));

const formatSavedAt = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const PracticeHoursDetailsPage = () => {
  const [practiceHours, setPracticeHours] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedAtLabel, setSavedAtLabel] = useState("");

  const { data: practiceHoursData } = usePracticeHours();
  const updateMutation = useUpdatePracticeHours();
  const toggleMutation = useTogglePracticeHourStatus();

  const practiceHoursRef = useRef([]);
  const isSavingRef = useRef(false);
  const hasQueuedSaveRef = useRef(false);
  const lastPersistedPayloadRef = useRef(null);

  /*
   * Every save is followed by a refetch, so the server's answer must not be allowed to
   * land on top of an edit that has not been posted yet: a change made while a request
   * is in flight would otherwise be replaced by the row the earlier request wrote, and
   * the queued follow-up would post it back. Server data is adopted on the first load,
   * and after that only when nothing is pending and local state matches what was last
   * persisted.
   */
  useEffect(() => {
    if (!practiceHoursData) return;

    const isFirstLoad = practiceHoursRef.current.length === 0;
    const isBusy = isSavingRef.current || hasQueuedSaveRef.current;
    const hasUnsavedChanges =
      serializeHours(practiceHoursRef.current) !==
      lastPersistedPayloadRef.current;

    if (!isFirstLoad && (isBusy || hasUnsavedChanges)) return;

    practiceHoursRef.current = practiceHoursData;
    lastPersistedPayloadRef.current = serializeHours(practiceHoursData);
    setPracticeHours(practiceHoursData);
  }, [practiceHoursData]);

  const errors = useMemo(
    () =>
      practiceHours.reduce(
        (acc, day) => ({ ...acc, [day.id]: getDayError(day) }),
        {}
      ),
    [practiceHours]
  );

  const markSaved = () => {
    setSavedAtLabel(formatSavedAt());
    setSaveStatus("saved");
  };

  const persistHours = async () => {
    // Read at request time, never a snapshot taken when the change happened.
    const hours = practiceHoursRef.current;

    if (hours.some((day) => !!getDayError(day))) {
      setSaveStatus("invalid");

      return;
    }

    const payload = hours.map(toPayloadDay);
    const serializedPayload = JSON.stringify(payload);

    if (serializedPayload === lastPersistedPayloadRef.current) return;

    setSaveStatus("saving");

    try {
      await updateMutation.mutateAsync(payload);
      lastPersistedPayloadRef.current = serializedPayload;
      markSaved();
    } catch {
      // useUpdatePracticeHours already reports the reason as an error toast.
      setSaveStatus("error");
    }
  };

  /*
   * The request replaces the whole week, so two of them in flight at once can land out
   * of order and the older state wins. Only one runs at a time; a change made while
   * one is in flight queues a single follow-up, which re-reads the live state.
   */
  const autoSave = async () => {
    if (isSavingRef.current) {
      hasQueuedSaveRef.current = true;

      return;
    }

    isSavingRef.current = true;

    try {
      await persistHours();
    } finally {
      isSavingRef.current = false;

      if (hasQueuedSaveRef.current) {
        hasQueuedSaveRef.current = false;
        autoSave();
      }
    }
  };

  const applyDayChange = (id, changes) => {
    const next = practiceHoursRef.current.map((day) =>
      day.id === id ? { ...day, ...changes } : day
    );

    practiceHoursRef.current = next;
    setPracticeHours(next);
  };

  const handleStarttime = (id, event) => {
    applyDayChange(id, { startTime: event.target.value });
    autoSave();
  };

  const handleEndtime = (id, event) => {
    applyDayChange(id, { endTime: event.target.value });
    autoSave();
  };

  /*
   * The switch persists on its own endpoint rather than with the week, so its failures
   * are reported through the same indicator — with no Save button left, a silent
   * failure would only show up as the switch snapping back on the next refetch.
   */
  const handleToggle = (id) => {
    setSaveStatus("saving");
    toggleMutation.mutate(id, {
      onSuccess: markSaved,
      onError: () => setSaveStatus("error"),
    });
  };

  return (
    <div className={styles.content}>
      <PageHeader title="Practice Hours">
        <SaveStatus status={saveStatus} savedAtLabel={savedAtLabel} />
      </PageHeader>

      <section className={styles.section}>
        <div className={styles.week} data-testid="practice-hours-week">
          {practiceHours.map((item) => (
            <div
              key={item.id}
              className={`${styles.dayCard} ${item.disabled ? styles.dayCardDisabled : ""}`}
              data-testid="practice-hours-day"
            >
              <h6 className={styles.dayName}>{item.day}</h6>

              <div className={styles.toggleRow}>
                <input
                  className={styles.toggleInput}
                  type="checkbox"
                  role="switch"
                  aria-label={`${item.day} open`}
                  checked={!item.disabled}
                  onChange={() => handleToggle(item.id)}
                />
                <span className={styles.toggleLabel}>
                  {!item.disabled ? "Open" : "Closed"}
                </span>
              </div>

              <div className={styles.timeField}>
                <span className={styles.timeLabel}>from</span>
                <input
                  type="time"
                  onChange={(event) => handleStarttime(item.id, event)}
                  className={styles.timeInput}
                  aria-label={`${item.day} opening time`}
                  value={convertString(item.startTime)}
                />
              </div>

              <div className={styles.timeField}>
                <span className={styles.timeLabel}>to</span>
                <input
                  type="time"
                  onChange={(event) => handleEndtime(item.id, event)}
                  className={styles.timeInput}
                  aria-label={`${item.day} closing time`}
                  value={convertString(item.endTime)}
                />
              </div>

              {!!errors[item.id] && (
                <div className={styles.error}>{errors[item.id]}</div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export { PracticeHoursDetailsPage };
